/**
 * PythonEHQBridge - Bridge between Python and EHQ JavaScript services
 *
 * This helper manages the integration between Python code running in Pyodide
 * and EHQ's JavaScript services, enabling seamless communication in both directions.
 */

import { GeneralHelpers } from '../../../helpers/general.helper';
import { PythonHelper } from '../helpers/python.helper';
import {
  CUSTOM_METHODS_EXPORT,
  CUSTOM_METHODS_IMPORT,
  EHQ,
  EHQ_UPPER,
  ARROW_FLIGHT,
  ARROW_FLIGHT_COMPAT,
  ARROW_FLIGHT_SHORT,
  BOX,
  BOX_UPPER
} from '../../../constants/additional-methods.constants';
import {
  EHQServicesConfig,
  EHQMethodConfig,
  PythonEHQBridgeConfig,
  EHQBridgeState,
  EHQBridgeStats
} from '../interfaces/python-ehq.interface';
import { setupWasmPythonBindings } from '../../wasm/wasm-module/wasm-bindings.helper';
import { WasmModuleService } from '../../wasm/wasm-module/wasm-module.service';
import { PYTHON_DIRECTORIES, FileLists } from '../interfaces/python.interface';
import { ResponseType } from '../../../constants/general.constants';
import { FileTypeHelpers } from '../../../helpers/file-type.helper';
import { SimpleUIService } from '../../simple-ui/simple-ui.service';
import { ExecutionContext } from '@app/shared/interfaces/chunk/chunk-context.interface';

export class PythonEHQBridge {
  // `python` (the Pyodide runtime) and `pyBytes` (a PyProxy `bytes` constructor)
  // are dynamic PyProxy-backed handles whose surface Pyodide's own types only
  // loosely describe; kept as `any` per the audit (partly inherent) and ADR-0005
  // (Pyodide typing is revisited with the Worker-isolation work, not this pass).
  private readonly python: any;
  private readonly services: EHQServicesConfig;
  private chunkContext?: ExecutionContext;
  private readonly enableLogging: boolean;
  private pyBytes: any;

  // Bridge state
  private mainHandler: Record<string, any> = {};
  private readonly registeredModules: Set<string> = new Set();
  private projectFileNames: string[] = [];
  private stats: EHQBridgeStats = {
    methodCallsCount: 0,
    errorCount: 0,
    registeredModulesCount: 0
  };

  // External modules namespaces — auto-derived from CUSTOM_METHODS_EXPORT/IMPORT
  // so any namespace declared in additional-methods.constants.ts is automatically
  // exposed to Python step runtime. Aliases (uppercase + arrow short forms) are
  // added explicitly because they don't appear as globalName in constants.
  // Mirrors the JS-bridge pattern in javascript.service.ts via getUniqueGlobalNames().
  public static readonly JS_EXTERNAL_MODULES_NAMESPACES = (() => {
    const fromConstants = new Set<string>([
      ...CUSTOM_METHODS_EXPORT.map(m => m.globalName),
      ...CUSTOM_METHODS_IMPORT.map(m => m.globalName),
    ]);
    fromConstants.add(EHQ_UPPER);
    fromConstants.add(BOX_UPPER);
    fromConstants.add(ARROW_FLIGHT_COMPAT);
    fromConstants.add(ARROW_FLIGHT_SHORT);
    return Array.from(fromConstants);
  })();

  constructor(config: PythonEHQBridgeConfig) {
    this.python = config.python;
    this.services = config.services;
    this.chunkContext = config.chunkContext;
    this.enableLogging = config.enableLogging || false;
  }

  /**
   * Initialize the bridge between Python and JavaScript
   */
  public initializeBridge(): void {
    try {
      this.validateMethodConfigs();
      this.pyBytes = this.python.globals.get('bytes');
      this.setupExportMethods();
      this.setupImportMethods();
      this.registerDirectFsMethods();
      this.registerIntrospectionMethods();
      this.wrapPutApiMethods();
      this.addSnakeCaseAliases();
      this.registerJsExternalModules();
      this.registerGlobalFunctions();
      this.prefetchProjectFileList();

      // Give BoxService access to Pyodide FS so downloaded files are written to the
      // in-memory filesystem for immediate Python access
      if (this.services.boxService?.setPyodideFS) {
        this.services.boxService.setPyodideFS(this.python.FS);
      }
      if (this.services.routineService?.setPyodideFS) {
        this.services.routineService.setPyodideFS(this.python.FS);
      }
    } catch (error) {
      this.stats.errorCount++;
      throw error;
    }
  }

  /**
   * Fail-fast validation that every CUSTOM_METHODS_EXPORT/IMPORT entry references
   * a service present in EHQServicesConfig. Catches drift between
   * additional-methods.constants.ts and the services config at init time, not at
   * user-call time (which would surface as a runtime PyodideFuture error).
   */
  private validateMethodConfigs(): void {
    const missing: string[] = [];
    for (const m of [...CUSTOM_METHODS_EXPORT, ...CUSTOM_METHODS_IMPORT]) {
      if (!(m.serviceToUse in this.services)) {
        missing.push(`${m.globalName}.${m.method} → '${m.serviceToUse}' not in EHQServicesConfig`);
      }
    }
    if (missing.length > 0) {
      throw new Error(
        `EHQ bridge config invalid — services missing:\n  ${missing.join('\n  ')}`,
      );
    }
  }

  /**
   * Set up WASM module Python bindings (async — call after initializeBridge).
   * Generates Python classes from module manifests and injects them so
   * `from modules import image_processing` works in step code.
   */
  public async setupWasmModuleBindings(wasmModuleService: WasmModuleService): Promise<void> {
    try {
      await setupWasmPythonBindings(this.python, wasmModuleService);
    } catch (error) {
      console.error('[PythonEHQBridge] Failed to setup WASM module bindings:', error);
    }
  }

  /**
   * Setup export methods (Python calls JavaScript, sync)
   */
  private setupExportMethods(): void {
    CUSTOM_METHODS_EXPORT.forEach((methodConfig: EHQMethodConfig) => {
      this.registerBridgeMethod(methodConfig, 'export');
    });
  }

  /**
   * Setup import methods (JavaScript returns data to Python, async)
   */
  private setupImportMethods(): void {
    CUSTOM_METHODS_IMPORT.forEach((methodConfig: EHQMethodConfig) => {
      this.registerBridgeMethod(methodConfig, 'import');
    });
  }

  /**
   * Creates and registers a single bridge method for a given config.
   *
   * Export methods are synchronous and strip filesystem subdirectory prefixes
   * from the first argument so Python paths like 'files/out.json' become 'out.json'.
   *
   * Import methods are async and convert the JS result to a Python object via toPy
   * so step code can access it with standard Python dict/list semantics.
   */
  private registerBridgeMethod(
    methodConfig: EHQMethodConfig,
    direction: 'export' | 'import',
  ): void {
    const { globalName, method, serviceToUse, methodToCall, errorMode = 'rethrow' } = methodConfig;

    if (!this.mainHandler[globalName]) {
      this.mainHandler[globalName] = {};
    }

    // Shared error handler — classifies errors by errorMode (silent/nullable/rethrow)
    // and always surfaces the error in the chunk UI via addMessage('danger') so the
    // user sees a failure even when Python code does not await or try/except the call.
    // This replaces the previous uniform-rethrow behaviour that caused "PyodideFuture
    // exception was never retrieved" warnings for async rejections.
    const handleError = (error: unknown): unknown => {
      this.stats.errorCount++;
      const msg = error instanceof Error ? error.message : String(error);
      this.stats.lastError = msg;
      // A controlled halt (EHQ.abortJob/requestRetry/requestReview) flags one of
      // globalThis._abortRequested/_retryRequested/_reviewRequested before
      // throwing. That is not a failure — the step executor renders a clean
      // neutral outcome — so suppress the red console error + danger banner for
      // it. The executor resets these flags at step start, so a set flag here
      // belongs to THIS step. Still rethrow below to unwind the Python stack.
      const g = globalThis as Record<string, unknown>;
      const isControlledHalt =
        !!(g['_abortRequested'] || g['_retryRequested'] || g['_reviewRequested']);
      if (!isControlledHalt) {
        console.error(`[${globalName}.${method}]`, error);
        try {
          this.chunkContext?.addMessage?.(
            `${globalName}.${method}: ${msg}`,
            'danger',
          );
        } catch {
          // chunkContext may be cleaned up mid-flight — swallow.
        }
      }

      switch (errorMode) {
        case 'silent':
          return undefined;
        case 'nullable':
          return null;
        case 'rethrow':
        default:
          throw new Error(`${globalName}.${method}() failed: ${msg}`);
      }
    };

    if (direction === 'export') {
      // Export methods are synchronous from Python's POV but may return a Promise
      // under the hood (async services). The returned Promise must have a catch
      // handler wired up — otherwise async rejections become unretrieved futures.
      //
      // NOTE: every bridge handler registered on `mainHandler` takes
      // `(...args: any[])` (or a single dynamic arg): these are invoked FROM
      // Python with Pyodide PyProxy values, so their argument arrays are
      // inherently dynamic. Left as `any` per the audit / ADR-0005.
      this.mainHandler[globalName][method] = (...args: any[]) => {
        this.stats.methodCallsCount++;
        this.stats.lastMethodCall = `${globalName}.${method}`;

        try {
          const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, this.python));

          // Strip Python subdirectory prefixes from first argument (Filesystem Restructure)
          // Safe to apply to all methods - no-op if prefix not present
          // Prevents nested paths: 'files/output.json' → 'output.json'
          const filename = jsArgs[0];
          if (typeof filename === 'string') {
            const subdirPrefixes = [
              `${PYTHON_DIRECTORIES.FILES_SUBDIR}/`,
              `${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}/`,
              `${PYTHON_DIRECTORIES.PROJECT_SUBDIR}/`,
            ];
            for (const prefix of subdirPrefixes) {
              if (filename.startsWith(prefix)) {
                jsArgs[0] = filename.substring(prefix.length);
                break;
              }
            }
          }

          const service = this.services[serviceToUse as keyof EHQServicesConfig];
          if (!service) {
            throw new Error(`Service ${serviceToUse} not found in EHQ services config`);
          }

          const result = service[methodToCall](jsArgs, this.chunkContext, 'python');

          // For 'log' method, return Promise with double rAF to ensure browser repaint
          // This enables real-time UI updates when Python awaits ehq.log()
          // Double rAF guarantees: frame 1 = change detection, frame 2 = repaint complete
          if (method === 'log') {
            return new Promise(resolve => {
              requestAnimationFrame(() => requestAnimationFrame(() => resolve(true)));
            });
          }

          // For void/async methods returning a Promise (e.g. renderTable, saveFile,
          // saveFileFromUrl), wire up the rejection handler so unhandled rejections
          // don't surface as "PyodideFuture exception was never retrieved" warnings.
          if (result != null && typeof result.then === 'function') {
            return result.then(() => undefined, handleError);
          }
          // For void methods (result is undefined/null), return a resolved
          // Promise so Python `await` works. Pyodide can only await JS
          // Promises, not plain values like bool/None.
          if (result == null) return Promise.resolve(undefined);
          return result;
        } catch (error) {
          // handleError throws for rethrow mode (sync throw escapes before
          // Promise.resolve runs). For silent/nullable it returns
          // undefined/null — wrap in Promise.resolve so Python `await` on
          // the call still resolves cleanly instead of raising "object is
          // not awaitable".
          return Promise.resolve(handleError(error));
        }
      };
    } else {
      // Import methods are async — JavaScript returns data to Python
      this.mainHandler[globalName][method] = async (...args: any[]) => {
        this.stats.methodCallsCount++;
        this.stats.lastMethodCall = `${globalName}.${method}`;

        try {
          const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, this.python));

          const service = this.services[serviceToUse as keyof EHQServicesConfig];
          if (!service) {
            throw new Error(`Service ${serviceToUse} not found in EHQ services config`);
          }

          const result = await service[methodToCall](jsArgs, this.chunkContext, 'python');

          return this.convertJsResultToPython(result);
        } catch (error) {
          return handleError(error);
        }
      };
    }
  }

  /**
   * Convert a JS result to the appropriate Python type via Pyodide's toPy().
   * - null → None (cancellable imports like capturePhoto)
   * - undefined → true (matches enoch's behavior for void calls)
   * - Uint8Array/ArrayBuffer → Python bytes
   * - Blob → Python bytes (async; safety net for unconverted binary data)
   * - objects/arrays → Python dict/list
   * - primitives → returned as-is
   */
  private convertJsResultToPython(result: any): any {
    if (result === null) return null;
    if (result === undefined) return true;
    if (result instanceof Uint8Array || result instanceof ArrayBuffer) {
      const data = result instanceof ArrayBuffer ? new Uint8Array(result) : result;
      // toPy() produces memoryview; wrap in bytes() to match enoch's behavior
      return this.pyBytes(this.python.toPy(data));
    }
    // Blob: toPy() leaves it as JsProxy (not bytes). Extract buffer explicitly.
    // Currently all services pre-convert Blob→Uint8Array, but this is a safety net.
    if (typeof Blob !== 'undefined' && result instanceof Blob) {
      return result.arrayBuffer().then((buf: ArrayBuffer) =>
        this.pyBytes(this.python.toPy(new Uint8Array(buf)))
      );
    }
    if (typeof result === 'object') {
      return this.python.toPy(result);
    }
    return result;
  }

  /**
   * Search for a file across Pyodide FS subdirectories.
   * Returns the full path if found, null otherwise.
   * Search order matches enoch: files → default → project.
   */
  private findFileInFs(filename: string): string | null {
    const FS = this.python.FS;
    for (const subdir of PYTHON_DIRECTORIES.SEARCH_SUBDIRS) {
      try {
        const fullPath = `${PYTHON_DIRECTORIES.WORKING_DIR}/${subdir}/${filename}`;
        const stat = FS.stat(fullPath);
        if (FS.isFile(stat.mode)) return fullPath;
      } catch {
        // FS.stat throws if path doesn't exist
      }
    }
    return null;
  }

  /**
   * Register methods that operate directly on Pyodide's virtual filesystem
   * rather than routing through Angular services.
   */
  private registerDirectFsMethods(): void {
    if (!this.mainHandler[EHQ]) {
      this.mainHandler[EHQ] = {};
    }

    // EHQ.fileExists(path) — check if file exists in Pyodide FS.
    // Matches enoch's search order: workflow files → default files → project files.
    // Also checks the bridge's projectFileNames cache because project data files
    // are NOT mirrored into Pyodide /project/ subdir (only modules go there) —
    // see prefetchProjectFileList for how that cache is populated.
    this.mainHandler[EHQ]['fileExists'] = (path: any) => {
      const jsPath = PythonHelper.pythonToJs(path, this.python);
      if (this.findFileInFs(jsPath) !== null) return true;
      return this.projectFileNames.includes(jsPath);
    };

    // Wrap EHQ.saveFile() to also write to Pyodide FS.
    // The original handler persists to the backend via cacheService, but
    // the file doesn't appear in Pyodide's in-memory FS. Enoch's saveFile
    // writes to the local filesystem which IS Pyodide's FS, so fileExists()
    // works immediately after saveFile(). We mirror that by writing to the
    // files/ subdirectory before calling the original handler.
    const originalSaveFile = this.mainHandler[EHQ]['saveFile'];
    if (originalSaveFile) {
      this.mainHandler[EHQ]['saveFile'] = (...args: any[]) => {
        const filename = PythonHelper.pythonToJs(args[0], this.python);
        const content = PythonHelper.pythonToJs(args[1], this.python);
        if (filename && content != null) {
          try {
            const filePath = `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}/${filename}`;
            this.python.FS.writeFile(filePath, content);
          } catch (e) {
            console.warn('Failed to write to Pyodide FS:', e);
          }
        }
        return originalSaveFile(...args);
      };
    }

    // Enoch compatibility aliases:
    // enoch uses showAlert/showPrompt, ehq-web uses showSimpleAlert/showTextPrompt.
    // Snake_case aliases (show_alert, show_prompt) are created automatically
    // by addSnakeCaseAliases() which runs after this method.
    if (this.mainHandler[EHQ]['showSimpleAlert']) {
      this.mainHandler[EHQ]['showAlert'] = this.mainHandler[EHQ]['showSimpleAlert'];
    }
    if (this.mainHandler[EHQ]['showTextPrompt']) {
      this.mainHandler[EHQ]['showPrompt'] = this.mainHandler[EHQ]['showTextPrompt'];
    }

    // Wrap EHQ.getFile() to check Pyodide FS first before hitting the backend.
    // Files created by saveFile are in Pyodide FS but may not have reached
    // the backend yet.
    const originalGetFile = this.mainHandler[EHQ]['getFile'];
    if (originalGetFile) {
      const python = this.python;

      this.mainHandler[EHQ]['getFile'] = async (...args: any[]) => {
        const filename = PythonHelper.pythonToJs(args[0], python);
        const foundPath = this.findFileInFs(filename);

        if (foundPath) {
          const fileExtension = GeneralHelpers.fileExtensionFromString(filename);
          const fileType = GeneralHelpers.getFileType(fileExtension);
          const isBinary = FileTypeHelpers.getResponseType(fileType) !== ResponseType.Text;

          if (isBinary) {
            const data = python.FS.readFile(foundPath);
            return this.convertJsResultToPython(data);
          } else {
            return python.FS.readFile(foundPath, { encoding: 'utf8' });
          }
        }
        // Fall back to backend
        return originalGetFile(...args);
      };
    }

    // Wrap EHQ.listFiles() to return enoch's FileLists shape
    // ({ workflow, default, project }: Dict[str, List[str]]). Step code relies on
    // it (`listing = EHQ.listFiles(); listing.to_py(); listing.get("project")`).
    // 1:1 port of enoch's FileManager.listFiles() (filesystem/manager.ts): a
    // per-dir listDir() helper + explicit category→subdir assignment (files/ is
    // the "workflow" category). SYNC (enoch's is sync; an async wrapper returns a
    // PyodideFuture → "'PyodideFuture' object has no attribute 'get'").
    const listDir = (subdir: string): string[] => {
      const FS = this.python.FS;
      const dirPath = `${PYTHON_DIRECTORIES.WORKING_DIR}/${subdir}`;
      if (!FS.analyzePath(dirPath).exists) return [];
      const names: string[] = [];
      for (const name of FS.readdir(dirPath) as string[]) {
        if (name === '.' || name === '..') continue;  // Emscripten readdir includes these
        try {
          if (FS.isFile(FS.stat(`${dirPath}/${name}`).mode)) names.push(name);
        } catch (e) {
          console.warn('listFiles: failed to stat entry', `${dirPath}/${name}`, e);
        }
      }
      return names.sort((a, b) => a.localeCompare(b));
    };
    this.mainHandler[EHQ]['listFiles'] = (..._args: any[]) => {
      const result: FileLists = {
        workflow: listDir(PYTHON_DIRECTORIES.FILES_SUBDIR),
        default: listDir(PYTHON_DIRECTORIES.DEFAULT_SUBDIR),
        project: listDir(PYTHON_DIRECTORIES.PROJECT_SUBDIR),
      };
      return this.convertJsResultToPython(result);
    };

    // Wrap EHQ.listProjectFiles() — project data files are NOT in Pyodide FS
    // (only Python modules go to /project/), so we can't use the FS-readdir
    // shortcut from listFiles. Read the bridge-local cache populated by
    // prefetchProjectFileList. fileService.fileList CANNOT be used here —
    // fileService.listProjectFiles → getProjectFiles only updates the
    // private fileListCache Map, not the public fileList field (only the
    // legacy fileService.get() does that, and we don't want to depend on it).
    this.mainHandler[EHQ]['listProjectFiles'] = async (..._args: any[]) => {
      return this.convertJsResultToPython(this.projectFileNames);
    };
  }

  /**
   * Prefetch project file names into the bridge-local cache so user step
   * code calling ehq.listProjectFiles() / ehq.fileExists() gets the right
   * answer synchronously. We capture listProjectFiles' resolved value
   * directly because the function returns the names but only updates an
   * internal fileListCache Map (not the public fileList field).
   */
  private prefetchProjectFileList(): void {
    const fileService = this.services.fileService as
      | { listProjectFiles?: () => Promise<string[]> }
      | undefined;
    if (!fileService?.listProjectFiles) return;
    fileService.listProjectFiles()
      .then(names => {
        this.projectFileNames = [...names].sort((a, b) => a.localeCompare(b));
      })
      .catch((e: unknown) => {
        console.warn('[PythonEHQBridge] prefetch listProjectFiles failed:', e);
      });
  }

  /**
   * Register introspection methods (SCRUM-353).
   *
   * Registered directly (not through registerBridgeMethod) to avoid the
   * null → true coercion in convertJsResultToPython for methods that can
   * legitimately return None (getConnection, getStepInfo, getJobInfo).
   *
   * Must be called AFTER setupImportMethods() and BEFORE addSnakeCaseAliases()
   * so snake_case aliases are auto-generated.
   */
  private registerIntrospectionMethods(): void {
    if (!this.mainHandler[EHQ]) {
      this.mainHandler[EHQ] = {};
    }

    const introspectionService = this.services.introspectionBridgeService;
    if (!introspectionService) return;

    const python = this.python;

    // All methods are sync — reads from in-memory caches.
    // Matches enoch behavior so the same Python code works in both runtimes.
    this.mainHandler[EHQ]['listConnections'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      const result = introspectionService.listConnections(jsArgs);
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getConnection'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      const result = introspectionService.getConnection(jsArgs);
      if (result === null) return null;
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['listSteps'] = (..._args: any[]) => {
      const result = introspectionService.listSteps();
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getStepInfo'] = (..._args: any[]) => {
      const result = introspectionService.getStepInfo(this.chunkContext);
      if (result === null) return null;
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getJobInfo'] = () => {
      const result = introspectionService.getJobInfo();
      if (result === null) return null;
      return python.toPy(result);
    };

    // Phase 3: Additional introspection methods
    this.mainHandler[EHQ]['getWorkflowInfo'] = (..._args: any[]) => {
      const result = introspectionService.getWorkflowInfo();
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getProjectInfo'] = (..._args: any[]) => {
      const result = introspectionService.getProjectInfo();
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getDeploymentInfo'] = (..._args: any[]) => {
      const result = introspectionService.getDeploymentInfo();
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['isFeatureEnabled'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      return introspectionService.isFeatureEnabled(jsArgs);
    };

    this.mainHandler[EHQ]['listObjectStorage'] = (..._args: any[]) => {
      const result = introspectionService.listObjectStorage();
      return python.toPy(result);
    };

    // Secrets metadata (SCRUM-584) — SYNC, served from the in-memory cache.
    // Strictly metadata; no values. getSecretValue (async) is registered via
    // the CUSTOM_METHODS_IMPORT pathway because it needs a server round-trip.
    this.mainHandler[EHQ]['listSecrets'] = (..._args: any[]) => {
      const result = introspectionService.listSecrets();
      return python.toPy(result);
    };

    this.mainHandler[EHQ]['getSecret'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      const result = introspectionService.getSecret(jsArgs);
      if (result === null) return null;
      return python.toPy(result);
    };

    // Phase 2: Control flow
    this.mainHandler[EHQ]['skipToStep'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      introspectionService.skipToStep(jsArgs, this.chunkContext);
    };

    // Enoch compatibility (SCRUM-254)
    this.mainHandler[EHQ]['getAuthToken'] = (..._args: any[]) => {
      return introspectionService.getAuthToken();
    };

    this.mainHandler[EHQ]['getApiUrl'] = (..._args: any[]) => {
      return introspectionService.getApiUrl();
    };

    // deleteFile alias → removeFile (enoch uses deleteFile)
    this.mainHandler[EHQ]['deleteFile'] = this.mainHandler[EHQ]['removeFile'];

    // showSimpleAlert: sync, returns false (matches enoch — SCRUM-255)
    // Dialog still shows in browser (fire-and-forget) but Python doesn't wait.
    const simpleUIService = this.services.simpleUIService;
    this.mainHandler[EHQ]['showSimpleAlert'] = (...args: any[]) => {
      const jsArgs = args.map(arg => PythonHelper.pythonToJs(arg, python));
      if (simpleUIService) {
        simpleUIService.showSimpleAlert(jsArgs, this.chunkContext)
          ?.catch((err: unknown) => console.error('showSimpleAlert failed:', err));
      }
      return false;
    };
    this.mainHandler[EHQ]['showAlert'] = this.mainHandler[EHQ]['showSimpleAlert'];

    // Note: ehq.call (SCRUM-448) is now resolved via constants pathway.
    // The CUSTOM_METHODS_IMPORT entry references 'serviceCallService' which is
    // provided as an inline wrapper in python.service.ts (mirrors JS bridge).
    // No manual override needed here.
  }

  /**
   * Wrap Put API methods to auto-convert pyarrow Tables to IPC file bytes.
   * Provides parity with enoch's Python wrappers (service-wrappers.ts).
   *
   * Must be called AFTER setupImportMethods() and BEFORE addSnakeCaseAliases()
   * so that snake_case aliases (create_put, continue_put) automatically point
   * to the wrapped versions.
   */
  private wrapPutApiMethods(): void {
    const arrowHandler = this.mainHandler[ARROW_FLIGHT];
    if (!arrowHandler) return;

    const python = this.python;

    // Python code to convert a pyarrow.Table to IPC file format bytes.
    // Uses pa.ipc.new_file (IPC file format with ARROW1 footer) as required
    // by newton-rs. Stream format (new_stream) is rejected.
    const IPC_CONVERT = `
import pyarrow as pa
_ehq_sink = pa.BufferOutputStream()
_ehq_writer = pa.ipc.new_file(_ehq_sink, _ehq_put_table.schema)
_ehq_writer.write_table(_ehq_put_table)
_ehq_writer.close()
_ehq_put_ipc_bytes = _ehq_sink.getvalue().to_pybytes()
del _ehq_sink, _ehq_writer
`;

    /**
     * Check if a PyProxy argument is a pyarrow.Table by testing for .schema.
     * JS primitives return undefined for .schema; PyProxy objects without
     * the attribute throw AttributeError which we catch.
     */
    const isPyarrowTable = (arg: any): boolean => {
      if (!arg || typeof arg !== 'object') return false;
      try {
        return arg.schema !== undefined;
      } catch {
        return false;
      }
    };

    /**
     * Convert a pyarrow.Table to IPC file bytes (Uint8Array).
     * Returns the original value unchanged if not a pyarrow.Table.
     */
    const convertTableToIpc = (tableArg: any): any => {
      if (!isPyarrowTable(tableArg)) return tableArg;

      try {
        python.globals.set('_ehq_put_table', tableArg);
        python.runPython(IPC_CONVERT);
        const ipcProxy = python.globals.get('_ehq_put_ipc_bytes');
        const ipcBytes = ipcProxy.toJs();
        ipcProxy.destroy();
        python.globals.delete('_ehq_put_table');
        python.globals.delete('_ehq_put_ipc_bytes');
        return ipcBytes;
      } catch (error) {
        try { python.globals.delete('_ehq_put_table'); } catch { /* ignore */ }
        try { python.globals.delete('_ehq_put_ipc_bytes'); } catch { /* ignore */ }
        throw error;
      }
    };

    // Wrap createPut: args are (dataset_name, base_path, partition_cols,
    // batch_size, table_buffer, connection_name) — table_buffer at index 4
    const originalCreatePut = arrowHandler['createPut'];
    if (originalCreatePut) {
      arrowHandler['createPut'] = (...args: any[]) => {
        if (args.length > 4) {
          args[4] = convertTableToIpc(args[4]);
        }
        return originalCreatePut(...args);
      };
    }

    // Wrap continuePut: args are (put_id, table_buffer) — table_buffer at index 1
    const originalContinuePut = arrowHandler['continuePut'];
    if (originalContinuePut) {
      arrowHandler['continuePut'] = (...args: any[]) => {
        if (args.length > 1) {
          args[1] = convertTableToIpc(args[1]);
        }
        return originalContinuePut(...args);
      };
    }
  }

  /**
   * Convert camelCase to snake_case
   */
  private static toSnakeCase(str: string): string {
    return str.replaceAll(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  }

  /**
   * Add snake_case aliases for all camelCase methods on handler objects.
   * Enoch's Python runtime uses snake_case (e.g., list_cloud, perform_query),
   * but ehq-web registers camelCase methods (listCloud, performQuery).
   */
  private addSnakeCaseAliases(): void {
    // Manual aliases for names where the automatic camelCase→snake_case
    // conversion doesn't match enoch's Python wrapper names.
    // e.g. listDataSets → list_data_sets (auto) but enoch uses list_datasets
    const MANUAL_ALIASES: Record<string, string> = {
      list_data_sets: 'list_datasets',
      delete_data_set: 'delete_dataset',
      create_data_set: 'create_dataset',
      refresh_data_set_list: 'refresh_dataset_list',
    };

    for (const moduleName of Object.keys(this.mainHandler)) {
      const handler = this.mainHandler[moduleName];
      if (typeof handler !== 'object' || handler === null) continue;

      for (const key of Object.keys(handler)) {
        const snakeKey = PythonEHQBridge.toSnakeCase(key);
        if (snakeKey !== key && !(snakeKey in handler)) {
          handler[snakeKey] = handler[key];
        }
        // Add manual alias if one exists for this snake_case name
        const manualAlias = MANUAL_ALIASES[snakeKey];
        if (manualAlias && !(manualAlias in handler)) {
          handler[manualAlias] = handler[key];
        }
      }
    }
  }

  /**
   * Register external JS modules in Python.
   *
   * Each module is registered in two ways:
   * 1. registerJsModule() — makes `import MODULE` work
   * 2. python.globals.set() — makes bare `MODULE.method()` work without import
   *
   * Enoch injects all service wrappers via globals() (service-wrappers.ts),
   * so step code written for enoch uses bare names. We must match that.
   */
  private registerJsExternalModules(): void {
    PythonEHQBridge.JS_EXTERNAL_MODULES_NAMESPACES.forEach(moduleName => {
      if (this.mainHandler[moduleName]) {
        this.python.registerJsModule(moduleName, this.mainHandler[moduleName]);
        this.python.globals.set(moduleName, this.mainHandler[moduleName]);
        this.registeredModules.add(moduleName);
        this.stats.registeredModulesCount++;
      }
    });

    // Register alias: EHQ (uppercase) → same handler as ehq (lowercase)
    // Enoch exposes both 'EHQ' and 'ehq'; workflows use uppercase 'EHQ.saveFile(...)'
    if (this.mainHandler[EHQ]) {
      this.mainHandler[EHQ_UPPER] = this.mainHandler[EHQ];
      if (!this.registeredModules.has(EHQ_UPPER)) {
        this.python.registerJsModule(EHQ_UPPER, this.mainHandler[EHQ]);
        this.python.globals.set(EHQ_UPPER, this.mainHandler[EHQ]);
        this.registeredModules.add(EHQ_UPPER);
        this.stats.registeredModulesCount++;
      }
    }

    // Register alias: BOX (uppercase) → same handler as box (lowercase)
    if (this.mainHandler[BOX]) {
      this.mainHandler[BOX_UPPER] = this.mainHandler[BOX];
      if (!this.registeredModules.has(BOX_UPPER)) {
        this.python.registerJsModule(BOX_UPPER, this.mainHandler[BOX]);
        this.python.globals.set(BOX_UPPER, this.mainHandler[BOX]);
        this.registeredModules.add(BOX_UPPER);
        this.stats.registeredModulesCount++;
      }
    }

    // Register aliases: arrowFlight and arrow → same handler as ARROW_FLIGHT
    if (this.mainHandler[ARROW_FLIGHT]) {
      this.mainHandler[ARROW_FLIGHT_COMPAT] = this.mainHandler[ARROW_FLIGHT];
      if (!this.registeredModules.has(ARROW_FLIGHT_COMPAT)) {
        this.python.registerJsModule(ARROW_FLIGHT_COMPAT, this.mainHandler[ARROW_FLIGHT]);
        this.python.globals.set(ARROW_FLIGHT_COMPAT, this.mainHandler[ARROW_FLIGHT]);
        this.registeredModules.add(ARROW_FLIGHT_COMPAT);
        this.stats.registeredModulesCount++;
      }
      this.mainHandler[ARROW_FLIGHT_SHORT] = this.mainHandler[ARROW_FLIGHT];
      if (!this.registeredModules.has(ARROW_FLIGHT_SHORT)) {
        this.python.registerJsModule(ARROW_FLIGHT_SHORT, this.mainHandler[ARROW_FLIGHT]);
        this.python.globals.set(ARROW_FLIGHT_SHORT, this.mainHandler[ARROW_FLIGHT]);
        this.registeredModules.add(ARROW_FLIGHT_SHORT);
        this.stats.registeredModulesCount++;
      }
    }
  }

  /**
   * Register global functions available to Python code.
   * In ehq-web (browser), getJobArguments() returns the `job_arguments`
   * notebook global when set (local testing); otherwise an empty dict.
   * Enoch provides the actual job arguments from the trigger payload.
   */
  private registerGlobalFunctions(): void {
    this.bindGetJobArgumentsGlobal();

    // Inject WEB = True so Python scripts can use `if WEB:` guards
    // to protect web-only API calls (skipToStep, fileExists, etc.).
    // In enoch headless, WEB is False (or absent).
    this.python.globals.set('WEB', true);
  }

  /**
   * Re-bind getJobArguments after notebook globals change (e.g. user added job_arguments).
   * Called from PythonService.addGlobalVariables() before each step run.
   */
  public refreshJobArgumentsBinding(): void {
    this.bindGetJobArgumentsGlobal();
  }

  private bindGetJobArgumentsGlobal(): void {
    const resolver = () => this.python.toPy(this.resolveJobArgumentsForPython());
    this.python.globals.set('getJobArguments', resolver);
    // Mirror onto __main__ so module_system.setup_base_namespace can inject it into step namespaces
    try {
      this.python.runPython(`
import __main__ as _ehq_main
_ehq_main.getJobArguments = getJobArguments
`);
    } catch {
      // Non-fatal — globals() fallback in setup_base_namespace may still work
    }
  }

  /**
   * Resolve job args for browser runs: workflow variable first, then `data.job_arguments`.
   */
  private resolveJobArgumentsForPython(): Record<string, any> {
    const variableList = this.getNotebookVariableList();
    const fromVariables = SimpleUIService.resolveJobArgumentsFromVariables(variableList);
    if (fromVariables && Object.keys(fromVariables).length > 0) {
      return fromVariables;
    }

    const fromSimpleUi =
      this.services.simpleUIService?.getJobArguments?.([], this.chunkContext) ?? {};
    if (fromSimpleUi && Object.keys(fromSimpleUi).length > 0) {
      return fromSimpleUi;
    }

    try {
      const dataGlobal = this.python.globals.get('data');
      if (!dataGlobal) {
        return {};
      }

      const dataJs =
        typeof dataGlobal.toJs === 'function'
          ? PythonHelper.pythonToJs(dataGlobal, this.python)
          : null;

      for (const key of ['job_arguments', 'jobArguments']) {
        let raw: unknown;
        if (dataJs && typeof dataJs === 'object' && key in (dataJs as object)) {
          raw = (dataJs as Record<string, unknown>)[key];
        } else if (typeof dataGlobal.get === 'function') {
          raw = dataGlobal.get(key);
        } else {
          continue;
        }
        if (raw === undefined || raw === null) {
          continue;
        }
        const converted =
          raw !== null && typeof raw === 'object' && typeof (raw as { toJs?: unknown }).toJs === 'function'
            ? PythonHelper.pythonToJs(raw, this.python)
            : raw;
        const parsed = SimpleUIService.parseJobArgumentsValue(converted);
        if (Object.keys(parsed).length > 0) {
          return parsed;
        }
      }
    } catch {
      // Pyodide data global may not be ready yet
    }

    return {};
  }

  /** Latest workflow variables (signal-backed when available). */
  private getNotebookVariableList(): Array<{ name?: string; value?: unknown }> {
    const svc = this.services.notebookVariablesService;
    if (!svc) {
      return [];
    }
    if (typeof svc.variableList$ === 'function') {
      return svc.variableList$() ?? [];
    }
    return svc.variableList ?? [];
  }

  /**
   * Register external JS modules for content and return import string
   */
  public registerExternalJSModules(content: string): string {
    const presentedModules = this.getPresentedExternalJSModules(content);
    let importsString = '';

    if (presentedModules.length > 0) {
      presentedModules.forEach(moduleName => {
        if (!this.registeredModules.has(moduleName) && this.mainHandler[moduleName]) {
          this.python.registerJsModule(moduleName, this.mainHandler[moduleName]);
          this.registeredModules.add(moduleName);
          this.stats.registeredModulesCount++;
        }
        importsString += `import ${moduleName}\n`;
      });
    }

    return `${importsString}${content}`;
  }

  /**
   * Unregister external JS modules
   */
  public unregisterExternalJSModules(content: string): void {
    const presentedModules = this.getPresentedExternalJSModules(content);

    if (presentedModules.length > 0) {
      presentedModules.forEach(moduleName => {
        if (this.registeredModules.has(moduleName)) {
          this.python.unregisterJsModule(moduleName);
          this.registeredModules.delete(moduleName);
          this.stats.registeredModulesCount--;
        }
      });
    }
  }

  /**
   * Get modules that are presented in the content
   */
  private getPresentedExternalJSModules(content: string): string[] {
    return PythonEHQBridge.JS_EXTERNAL_MODULES_NAMESPACES.filter(moduleName =>
      content.includes(`${moduleName}.`)
    );
  }

  /**
   * Update chunk context for bridge operations
   */
  public updateChunkContext(context: ExecutionContext): void {
    this.chunkContext = context;
  }

  /**
   * Get bridge state for monitoring
   */
  public getBridgeState(): EHQBridgeState {
    return {
      initialized: this.registeredModules.size > 0,
      registeredModules: new Set(this.registeredModules),
      exportMethodsCount: CUSTOM_METHODS_EXPORT.length,
      importMethodsCount: CUSTOM_METHODS_IMPORT.length,
      lastError: this.stats.lastError
    };
  }

  /**
   * Get bridge statistics
   */
  public getBridgeStats(): EHQBridgeStats {
    return { ...this.stats };
  }

  /**
   * Reset bridge statistics
   */
  public resetStats(): void {
    this.stats = {
      methodCallsCount: 0,
      errorCount: 0,
      registeredModulesCount: this.registeredModules.size,
      lastMethodCall: undefined,
      lastError: undefined
    };
  }

  /**
   * Cleanup bridge resources
   */
  public cleanup(): void {
    // Unregister all modules
    this.registeredModules.forEach(moduleName => {
      try {
        this.python.unregisterJsModule(moduleName);
      } catch (error) {
        console.warn('Could not unregister module during cleanup', { moduleName, error });
      }
    });

    // Release cached Python builtins
    if (this.pyBytes) {
      this.pyBytes.destroy();
      this.pyBytes = null;
    }

    // Clear state
    this.registeredModules.clear();
    this.mainHandler = {};
    this.resetStats();
  }

  /**
   * Get JS module names in both camelCase and snake_case for filtering
   * Handles naming conversion: arrowFlight → arrow_flight
   * @param cache - Optional cache object to store results
   * @returns Set of module names in both formats
   */
  static getJSModuleNamesSet(cache?: { value?: Set<string> }): Set<string> {
    // Return cached value if available
    if (cache?.value) {
      return cache.value;
    }

    const names = new Set<string>();

    for (const moduleName of PythonEHQBridge.JS_EXTERNAL_MODULES_NAMESPACES) {
      // Add camelCase version
      names.add(moduleName);

      // Convert to snake_case for Python imports
      // arrowFlight → arrow_flight
      const snakeCase = moduleName
        .replace(/([A-Z])/g, '_$1')  // Insert _ before capitals
        .toLowerCase()                // Convert to lowercase
        .replace(/^_/, '');           // Remove leading _ if any

      names.add(snakeCase);
    }

    // Store in cache if provided
    if (cache) {
      cache.value = names;
    }

    return names;
  }
}
